import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';
import { Icon } from './Icon';

interface Props {
  /** Accessible name of the trigger and the panel («Ayuda de la sección»). */
  label: string;
  /** What the panel shows. */
  children: React.ReactNode;
  className?: string;
}

/** Panel width (Tailwind `w-72`) — needed to right-align the fixed panel. */
const PANEL_WIDTH = 288;
/** Panel max height (Tailwind `max-h-80`) + gap — flip-up threshold. */
const PANEL_MAX = 328;
/** Keep the panel this far from the viewport edges. */
const MARGIN = 8;

/**
 * Small round "?" that opens a help panel on hover, focus or click — the ONE
 * help affordance of the app (Standards §14.8). `SectionHelp` is this with a
 * list of fields inside; a single field with a paragraph of help uses it
 * directly (the customer's tax id, for example).
 *
 * Accessibility: it's a disclosure — the trigger is a real button with
 * `aria-expanded` + `aria-controls` pointing at the panel; it opens on
 * focus/click (click is open-only so a tap doesn't flicker on touch) and
 * closes on Escape, blur, or outside-click.
 *
 * The panel is **portalled to `document.body`** with fixed positioning computed
 * from the trigger's rect, so it always renders on top and never gets clipped
 * by a modal's `overflow` (the visible bug: in "Nuevo filamento" the panel was
 * cut off at the modal edge). It right-aligns to the trigger and flips above
 * when there isn't room below — same approach as `DropdownSelect`.
 */
export default function HelpHint({ label, children, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  // Delay the close so the pointer can cross the small gap between the trigger
  // and the panel (and reach a tall panel to scroll it) without it vanishing.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Positioning (flip + viewport clamp + follow on scroll) comes from the
  // shared hook; this component used to re-implement the same maths by hand.
  const coords = useAnchoredPosition(triggerRef, open, {
    estimatedWidth: PANEL_WIDTH,
    estimatedHeight: PANEL_MAX,
    align: 'right',
    gap: 4,
  });

  // Clear any pending close timer on unmount.
  useEffect(() => cancelClose, []);

  return (
    <div
      ref={ref}
      className={`relative ${className}`}
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      onBlur={(e) => {
        // Close when keyboard focus leaves the trigger+panel (not when it
        // moves into the panel).
        const next = e.relatedTarget as Node;
        if (!ref.current?.contains(next) && !panelRef.current?.contains(next)) {
          setOpen(false);
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={openNow}
        onFocus={openNow}
        className="w-5 h-5 rounded-full flex items-center justify-center border border-border-subtle dark:border-border-subtle-dark text-slate-400 dark:text-slate-500 hover:text-primary hover:border-primary focus:text-primary focus:border-primary transition-colors"
      >
        <Icon name="help" className="!text-[14px]" aria-hidden />
      </button>
      {open && coords && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={label}
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
          style={{ position: 'fixed', left: coords.left, top: coords.top, bottom: coords.bottom }}
          className="z-[200] w-72 max-h-80 overflow-y-auto overscroll-contain rounded-lg border border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark shadow-xl p-3 text-left normal-case tracking-normal font-normal"
        >
          {children}
        </div>,
        document.body,
      )}
    </div>
  );
}
